import React from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";

/**
 * The one header for everything reachable while signed out: the landing
 * page, the collections directory, peak / landmark / trail / region pages,
 * press, route analyser. One link set everywhere (ATL-132, agreed with Jack
 * 2026-10-09): content pages, the try-it tool, and the two landing sections
 * a visitor actually hunts for (pricing, the apps). Add a link here when a
 * new public page exists; nowhere else.
 *
 * Below 760 px the links fold into a menu button. The component carries its
 * own CSS (not appshell.css) because the landing page does not load the app
 * shell stylesheet.
 *
 * `variant="landing"` floats the bar over the landing hero (fixed, gradient
 * fade) instead of sitting in the document flow with a border.
 */

// Plain anchors for /guides (static pages prerendered outside the SPA route
// tree, so the browser must do a full navigation) and for the two landing
// sections: from "/" the fragment just scrolls, from anywhere else it loads
// the landing at that section.
//
// Order = product relevance (Jack, 2026-10-09): the record itself
// (collections, regions), the try-it tool, the commercial pair, then the
// editorial content. "Blog" rather than "Guides" because the latter reads
// as app documentation; the /guides path stays (prerendered, in the sitemap).
const LINKS: { label: string; to?: "/collections" | "/regions" | "/route-analyser"; href?: string }[] = [
  { label: "Collections", to: "/collections" },
  { label: "Regions", to: "/regions" },
  { label: "Check a route", to: "/route-analyser" },
  { label: "Apps", href: "/#apps" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Blog", href: "/guides" },
];

const CSS = `
.atlas-gtb{ display:flex; align-items:center; justify-content:space-between; gap:20px; padding:14px 28px; flex-shrink:0; font-family:var(--font-sans); }
.atlas-gtb--page{ border-bottom:1px solid var(--border-subtle); background:var(--bg-base); }
.atlas-gtb--landing{ position:fixed; top:0; left:0; right:0; z-index:20; background:linear-gradient(rgba(9,12,16,.86), rgba(9,12,16,0)); }
.atlas-gtb__left{ display:flex; align-items:center; gap:28px; min-width:0; }
.atlas-gtb__links{ display:flex; align-items:center; gap:20px; }
.atlas-gtb__link{ font-size:14px; font-weight:500; color:var(--text-secondary); text-decoration:none; white-space:nowrap; }
.atlas-gtb__link:hover, .atlas-gtb__link[data-status="active"]{ color:var(--text-primary); }
.atlas-gtb__right{ display:flex; align-items:center; gap:18px; flex:0 0 auto; }
.atlas-gtb__login{ font-size:14px; font-weight:500; color:var(--text-secondary); text-decoration:none; }
.atlas-gtb--landing .atlas-gtb__login{ color:var(--text-primary); }
.atlas-gtb__cta{
  display:inline-flex; align-items:center; height:var(--control-sm); padding:0 16px; border-radius:var(--radius-md);
  background:var(--accent); color:var(--text-on-gold); font-size:13px; font-weight:600; text-decoration:none; white-space:nowrap;
}
.atlas-gtb__burger{
  display:none; align-items:center; justify-content:center; width:36px; height:36px; padding:0;
  border:1px solid var(--border-subtle); border-radius:var(--radius-md); background:var(--surface-card);
  color:var(--text-primary); cursor:pointer;
}
.atlas-gtb__panel{
  display:none; position:fixed; left:0; right:0; z-index:30; padding:4px 16px 8px;
  background:var(--bg-base); border-bottom:1px solid var(--border-subtle); box-shadow:var(--shadow-lg);
}
.atlas-gtb__panel a{
  display:block; padding:12px 6px; border-bottom:1px solid var(--border-subtle);
  font-size:16px; font-weight:500; color:var(--text-primary); text-decoration:none;
}
.atlas-gtb__panel a:last-child{ border-bottom:none; }
@media (max-width:760px){
  .atlas-gtb{ padding:12px 16px; }
  .atlas-gtb__links, .atlas-gtb__login{ display:none; }
  .atlas-gtb__burger{ display:inline-flex; }
  .atlas-gtb__panel[data-open="true"]{ display:block; }
}
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-gtb-css")) {
    const s = document.createElement("style");
    s.id = "atlas-gtb-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

function NavLink({ l, className, onClick }: { l: (typeof LINKS)[number]; className: string; onClick?: () => void }) {
  return l.to ? (
    <Link to={l.to} className={className} onClick={onClick}>
      {l.label}
    </Link>
  ) : (
    <a href={l.href} className={className} onClick={onClick}>
      {l.label}
    </a>
  );
}

export function GuestTopBar({
  variant = "page",
  ctaLabel = "Join Atlas",
}: {
  variant?: "page" | "landing";
  ctaLabel?: string;
} = {}) {
  ensure();
  const [open, setOpen] = React.useState(false);
  const barRef = React.useRef<HTMLDivElement>(null);
  const [panelTop, setPanelTop] = React.useState(0);

  // The panel hangs just under the bar wherever the bar sits (in flow on
  // pages, fixed on the landing).
  React.useEffect(() => {
    if (!open) return;
    const rect = barRef.current?.getBoundingClientRect();
    setPanelTop(rect ? rect.bottom : 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <div ref={barRef} className={`atlas-gtb atlas-gtb--${variant}`}>
        <div className="atlas-gtb__left">
          <Link to="/" style={{ display: "flex", alignItems: "center", flex: "0 0 auto" }} onClick={close}>
            <img className="atlas-logo" src="/atlas-logo.svg" alt="Atlas" style={{ height: variant === "landing" ? 26 : 22 }} />
          </Link>
          <nav className="atlas-gtb__links" aria-label="Site">
            {LINKS.map((l) => (
              <NavLink key={l.label} l={l} className="atlas-gtb__link" />
            ))}
          </nav>
        </div>
        <div className="atlas-gtb__right">
          <Link to="/login" className="atlas-gtb__login">
            Log in
          </Link>
          <Link to="/signup" className="atlas-gtb__cta">
            {ctaLabel}
          </Link>
          <button
            type="button"
            className="atlas-gtb__burger"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="atlas-gtb-panel"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <nav id="atlas-gtb-panel" className="atlas-gtb__panel" data-open={open} aria-label="Site menu" style={{ top: panelTop }}>
        {LINKS.map((l) => (
          <NavLink key={l.label} l={l} className="" onClick={close} />
        ))}
        {/* The gold CTA stays in the bar itself on phones, so the panel only
            needs the one account link it hides. */}
        <Link to="/login" onClick={close}>
          Log in
        </Link>
      </nav>
    </>
  );
}
